import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAtom } from 'jotai';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import { allCarsAtom } from '../atoms/allCarsAtom';
import type Car from '../atoms/allCarsAtom';
import { API_BASE, parsePitTimeSeconds } from './scoring-types';

// ---------------------------------------------------------------------------
// Event type registry
// ---------------------------------------------------------------------------
type EventGroup = 'Pit' | 'Penalty' | 'Status';

interface EventTypeDef {
  id: string;
  group: EventGroup;
  label: string;
}

const EVENT_TYPES: EventTypeDef[] = [
  // Pit events — split into Start (button press when car enters pit box) and
  // End (button press + official radioed-in duration when car leaves).
  { id: 'PIT_DC_START', group: 'Pit', label: 'Driver Change — Start (car in)' },
  { id: 'PIT_DC_END', group: 'Pit', label: 'Driver Change — End (car out)' },
  { id: 'PIT_TIRE_START', group: 'Pit', label: 'Tire Change — Start' },
  { id: 'PIT_TIRE_END', group: 'Pit', label: 'Tire Change — End' },
  { id: 'PIT_MECH_BF_START', group: 'Pit', label: 'Mech Black Flag — Start' },
  { id: 'PIT_MECH_BF_END', group: 'Pit', label: 'Mech Black Flag — End' },
  { id: 'PIT_OTHER_START', group: 'Pit', label: 'Free Pit — Start' },
  { id: 'PIT_OTHER_END', group: 'Pit', label: 'Free Pit — End' },
  { id: 'PIT_CHARGED_START', group: 'Pit', label: 'Charged Pit — Start' },
  { id: 'PIT_CHARGED_END', group: 'Pit', label: 'Charged Pit — End' },
  // Penalty events
  { id: 'CONE', group: 'Penalty', label: 'Cone Down (+2s)' },
  { id: 'OFF_COURSE', group: 'Penalty', label: 'Off-Course (+20s)' },
  { id: 'MISSED_SLALOM', group: 'Penalty', label: 'Missed Slalom (+20s)' },
  { id: 'FLAG_DISOBEY', group: 'Penalty', label: 'Did Not Obey Flag (+60s)' },
  { id: 'OVER_DRIVING', group: 'Penalty', label: 'Over-Driving After Flag (+60s)' },
  { id: 'RUN_OUT_OF_ORDER', group: 'Penalty', label: 'Running Out of Order (+120s)' },
  { id: 'EXTRA_PERSON', group: 'Penalty', label: 'Extra Person in DC Area (-20 pts ea.)' },
  { id: 'EXCESS_TIRE_TIME', group: 'Penalty', label: 'Excess Tire Change Time' },
  { id: 'OTHER', group: 'Penalty', label: 'Other Penalty (time)' },
  { id: 'CONTACT_DQ', group: 'Penalty', label: 'Contact — Disqualify' },
  { id: 'ENERGY_VIOLATION', group: 'Penalty', label: 'Energy Violation — DNF' },
  // Status events
  { id: 'STATUS_ON_TRACK', group: 'Status', label: 'Mark as On Track' },
  { id: 'STATUS_FINISHED', group: 'Status', label: 'Mark as Finished' },
  { id: 'STATUS_DNF', group: 'Status', label: 'Mark as DNF' },
  { id: 'STATUS_DQ', group: 'Status', label: 'Mark as DQ' },
];

// Fixed-time penalty rules (seconds). Keep in sync with backend scoringConfig.
const PENALTY_TIME_DEFAULTS: Record<string, number> = {
  CONE: 2,
  OFF_COURSE: 20,
  MISSED_SLALOM: 20,
  FLAG_DISOBEY: 60,
  OVER_DRIVING: 60,
  RUN_OUT_OF_ORDER: 120,
};

// Free-time thresholds (seconds). Mirror fh-monitor-backend/services/scoring/config.ts
// (dcAllowedTimeSec / dcRestartTimeSec). Used by the "enter only time over
// allowance" UI toggle below, and also as the "within legal" sentinel we
// submit when an operator leaves the field blank.
const DC_ALLOWED_TIME_SEC = 180;
const DC_RESTART_TIME_SEC = 120;

interface FieldSpec {
  key: string;
  label: string;
  type: 'number' | 'text';
  required?: boolean;
  helper?: string;
  /**
   * If set, renders a "Enter time over allowance" switch beneath the input.
   * In "over" mode the operator enters the excess past this threshold (in
   * seconds) and we submit `threshold + entered` to the backend. A blank
   * value in either mode means "within legal time constraints" and we
   * submit the threshold (or omit the field for RestartTime).
   */
  overAllowanceSec?: number;
  /**
   * Optional override for the helper text to show while the "over" switch
   * is on (defaults to `helper`).
   */
  overHelper?: string;
  /** Optional override label while the "over" switch is on. */
  overLabel?: string;
}

function getFieldsFor(eventId: string): FieldSpec[] {
  switch (eventId) {
    // Start events: no fields — backend stamps StartTime = NOW() and auto-
    // assigns DC # from existing pit count.
    case 'PIT_DC_START':
    case 'PIT_TIRE_START':
    case 'PIT_MECH_BF_START':
    case 'PIT_OTHER_START':
    case 'PIT_CHARGED_START':
      return [];
    case 'PIT_DC_END':
      return [
        // text (not number) so the operator can type "2:55" as well as "175".
        // PitTime and RestartTime are both optional: a blank field means
        // "within legal constraints" (D7.13.6 / D7.13.8) and will be
        // submitted as the threshold (or omitted) so no excess penalty
        // is applied.
        {
          key: 'PitTime',
          label: 'DC Time',
          type: 'text',
          helper: `Seconds or m:ss. Blank = within ${DC_ALLOWED_TIME_SEC}s allowance.`,
          overAllowanceSec: DC_ALLOWED_TIME_SEC,
          overLabel: 'DC Time Over Allowance',
          overHelper: `Seconds or m:ss past the ${DC_ALLOWED_TIME_SEC}s allowance`,
        },
        {
          key: 'RestartTime',
          label: 'Restart Time',
          type: 'text',
          helper: `Seconds or m:ss. Blank = within ${DC_RESTART_TIME_SEC}s allowance.`,
          overAllowanceSec: DC_RESTART_TIME_SEC,
          overLabel: 'Restart Time Over Allowance',
          overHelper: `Seconds or m:ss past the ${DC_RESTART_TIME_SEC}s allowance`,
        },
      ];
    case 'PIT_TIRE_END':
    case 'PIT_MECH_BF_END':
    case 'PIT_OTHER_END':
    case 'PIT_CHARGED_END':
      return [{ key: 'PitTime', label: 'Official Pit Time', type: 'text', required: true, helper: 'Seconds or m:ss (radioed in)' }];
    case 'CONE':
      return [
        { key: 'Description', label: 'Location (e.g. turn 3)', type: 'text' },
        { key: 'Count', label: 'Number of cones', type: 'number', helper: 'Blank = 1 cone' },
      ];
    case 'OFF_COURSE':
      return [{ key: 'Description', label: 'Location (e.g. turn 5)', type: 'text' }];
    case 'FLAG_DISOBEY':
    case 'OVER_DRIVING':
      return [{ key: 'Description', label: 'Flag colour (e.g. yellow)', type: 'text', required: true }];
    case 'MISSED_SLALOM':
    case 'RUN_OUT_OF_ORDER':
    case 'CONTACT_DQ':
    case 'ENERGY_VIOLATION':
      return [];
    case 'EXTRA_PERSON':
      return [{ key: 'Count', label: 'Number of extra persons', type: 'number', required: true, helper: '-20 pts each' }];
    case 'EXCESS_TIRE_TIME':
      return [{ key: 'TimePenalty', label: 'Excess seconds', type: 'number', required: true }];
    case 'OTHER':
      return [
        { key: 'Description', label: 'Description', type: 'text', required: true },
        { key: 'TimePenalty', label: 'Time penalty (seconds)', type: 'number', required: true },
      ];
    default:
      return [];
  }
}

// ---------------------------------------------------------------------------
// Submission
// ---------------------------------------------------------------------------
// Maps an EventCard pit event id (with _START/_END suffix) to the backend
// PitType enum and which endpoint to hit.
interface PitEventMeta { type: string; phase: 'start' | 'end' }
const PIT_EVENT_MAP: Record<string, PitEventMeta> = {
  PIT_DC_START: { type: 'DC', phase: 'start' },
  PIT_DC_END: { type: 'DC', phase: 'end' },
  PIT_TIRE_START: { type: 'TIRE', phase: 'start' },
  PIT_TIRE_END: { type: 'TIRE', phase: 'end' },
  PIT_MECH_BF_START: { type: 'MECH_BF', phase: 'start' },
  PIT_MECH_BF_END: { type: 'MECH_BF', phase: 'end' },
  PIT_OTHER_START: { type: 'OTHER', phase: 'start' },
  PIT_OTHER_END: { type: 'OTHER', phase: 'end' },
  PIT_CHARGED_START: { type: 'CHARGED', phase: 'start' },
  PIT_CHARGED_END: { type: 'CHARGED', phase: 'end' },
};

const STATUS_MAP: Record<string, string> = {
  STATUS_ON_TRACK: 'On Track',
  STATUS_FINISHED: 'Finished',
  STATUS_DNF: 'DNF',
  STATUS_DQ: 'DQ',
};

/**
 * Resolve the DC official pit time from the form values. The DC time is
 * optional: a blank input is taken to mean "within the free allowance"
 * and we submit the threshold itself so the backend classifies the pit
 * as free with zero excess. When the operator flips the "over allowance"
 * switch, the entered value is treated as the overage and added to the
 * threshold.
 */
function resolveDcPitTime(rawValue: string, isOverMode: boolean): number {
  const trimmed = rawValue.trim();
  if (!trimmed) return DC_ALLOWED_TIME_SEC;
  const parsed = parsePitTimeSeconds(trimmed);
  if (!Number.isFinite(parsed)) {
    throw new Error(`Could not parse "${rawValue}" as seconds or m:ss`);
  }
  return isOverMode ? DC_ALLOWED_TIME_SEC + parsed : parsed;
}

/**
 * Resolve the restart time. Blank means "within legal constraints" and
 * we omit the field entirely (returns null). In "over" mode the entered
 * value is the overage and is added to the restart threshold. Accepts
 * the same seconds-or-m:ss format as the DC pit time input.
 */
function resolveRestartTime(rawValue: string, isOverMode: boolean): number | null {
  const trimmed = rawValue.trim();
  if (!trimmed) return null;
  const parsed = parsePitTimeSeconds(trimmed);
  if (!Number.isFinite(parsed)) {
    throw new Error(`Could not parse "${rawValue}" as seconds or m:ss`);
  }
  return isOverMode ? DC_RESTART_TIME_SEC + parsed : parsed;
}

function parsePositiveCount(rawValue: string | undefined, label: string): number {
  const trimmed = rawValue?.trim() ?? '';
  if (!trimmed) return 1;

  const count = Number(trimmed);
  if (!Number.isInteger(count) || count < 1) {
    throw new Error(`${label} must be a whole number of 1 or more`);
  }
  return count;
}

async function submitPit(
  carNumber: number,
  eventId: string,
  values: Record<string, string>,
  overModes: Record<string, boolean>,
) {
  const meta = PIT_EVENT_MAP[eventId];
  if (!meta) throw new Error(`Unknown pit event: ${eventId}`);

  if (meta.phase === 'start') {
    await axios.post(`${API_BASE}/api/scoring/pits/start`, {
      CarNumber: carNumber,
      PitType: meta.type,
    });
    return;
  }

  let pitTimeSec: number;
  if (eventId === 'PIT_DC_END') {
    pitTimeSec = resolveDcPitTime(values.PitTime ?? '', !!overModes.PitTime);
  } else {
    pitTimeSec = parsePitTimeSeconds(values.PitTime ?? '');
    if (!Number.isFinite(pitTimeSec)) {
      throw new Error(`Could not parse "${values.PitTime}" as seconds or m:ss`);
    }
  }

  const body: Record<string, unknown> = {
    CarNumber: carNumber,
    PitType: meta.type,
    PitTime: pitTimeSec,
  };
  if (eventId === 'PIT_DC_END') {
    const restart = resolveRestartTime(values.RestartTime ?? '', !!overModes.RestartTime);
    if (restart != null) body.RestartTime = restart;
  }
  await axios.post(`${API_BASE}/api/scoring/pits/end`, body);
}

async function submitPenalty(carNumber: number, eventId: string, values: Record<string, string>) {
  let timePenalty = 0;
  let pointPenalty = 0;
  let description: string | null = values.Description?.trim() || null;

  if (eventId in PENALTY_TIME_DEFAULTS) {
    timePenalty = PENALTY_TIME_DEFAULTS[eventId];
  } else if (values.TimePenalty) {
    timePenalty = parseFloat(values.TimePenalty);
  }

  if (eventId === 'CONE') {
    const count = parsePositiveCount(values.Count, 'Number of cones');
    timePenalty = PENALTY_TIME_DEFAULTS.CONE * count;
    const location = values.Description?.trim();
    description = location
      ? `${count} cone(s) down - ${location}`
      : `${count} cone(s) down`;
  }

  if (eventId === 'EXTRA_PERSON') {
    const count = parsePositiveCount(values.Count, 'Number of extra persons');
    pointPenalty = -20 * count;
    description = `${count} extra person(s) in DC area`;
  }

  await axios.post(`${API_BASE}/api/scoring/penalties`, {
    CarNumber: carNumber,
    PenaltyType: eventId,
    LapNumber: null,
    Description: description,
    TimePenalty: timePenalty,
    PointPenalty: pointPenalty,
    CreatedBy: 'organizer',
  });

  // Auto-state transitions
  if (eventId === 'CONTACT_DQ') {
    await axios.post(`${API_BASE}/api/setStatus`, { Status: 'DQ', CarNumber: carNumber });
  } else if (eventId === 'ENERGY_VIOLATION') {
    await axios.post(`${API_BASE}/api/setStatus`, { Status: 'DNF', CarNumber: carNumber });
  }
}

async function submitStatus(carNumber: number, eventId: string) {
  await axios.post(`${API_BASE}/api/setStatus`, {
    Status: STATUS_MAP[eventId],
    CarNumber: carNumber,
  });
}

async function submitEvent(
  carNumber: number,
  event: EventTypeDef,
  values: Record<string, string>,
  overModes: Record<string, boolean>,
) {
  switch (event.group) {
    case 'Pit': return submitPit(carNumber, event.id, values, overModes);
    case 'Penalty': return submitPenalty(carNumber, event.id, values);
    case 'Status': return submitStatus(carNumber, event.id);
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function EventCard() {
  const [allCars] = useAtom(allCarsAtom);

  const [selectedCar, setSelectedCar] = useState<Car | null>(null);
  const [eventType, setEventType] = useState<EventTypeDef | null>(null);
  const [carInput, setCarInput] = useState('');
  const [eventInput, setEventInput] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  // Per-field toggle: when true, the operator is entering time OVER the
  // free allowance (rather than the full pit/restart time). Reset every
  // time the event type changes.
  const [overModes, setOverModes] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const fields = useMemo(
    () => (eventType ? getFieldsFor(eventType.id) : []),
    [eventType],
  );

  const canSubmit =
    !!selectedCar &&
    !!eventType &&
    !submitting &&
    fields.every((f) => !f.required || (values[f.key] && values[f.key].trim() !== ''));

  const carLabel = (c: Car) => `${c.CarNumber} — ${c.Institution}`;

  // Match typed text against CarNumber or Institution so the operator can
  // find a car by either. Case- and whitespace-insensitive.
  const carFilter = createFilterOptions<Car>({
    stringify: (c) => `${c.CarNumber} ${c.Institution}`,
    trim: true,
    ignoreCase: true,
  });

  const eventFilter = createFilterOptions<EventTypeDef>({
    stringify: (e) => `${e.group} ${e.label} ${e.id}`,
    trim: true,
    ignoreCase: true,
  });

  useEffect(() => {
    if (status?.kind !== 'success') return undefined;
    const timer = window.setTimeout(() => setStatus(null), 3000);
    return () => window.clearTimeout(timer);
  }, [status]);

  const handleSubmit = async () => {
    if (!selectedCar || !eventType) return;
    setSubmitting(true);
    setStatus(null);
    try {
      await submitEvent(selectedCar.CarNumber, eventType, values, overModes);
      setStatus({
        kind: 'success',
        msg: `${eventType.label} recorded for car ${selectedCar.CarNumber}`,
      });
      setValues({});
      setOverModes({});
      setEventType(null);
      setSelectedCar(null);
      setCarInput('');
      setEventInput('');
      // LiveBoard picks up the change via websocket — no manual refresh needed.
    } catch (err) {
      const message =
        axios.isAxiosError(err) && err.response?.data
          ? typeof err.response.data === 'string'
            ? err.response.data
            : JSON.stringify(err.response.data)
          : (err as Error).message;
      setStatus({ kind: 'error', msg: `Failed: ${message}` });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card sx={{ m: 1 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Record Event
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Stack
          spacing={2}
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ md: 'flex-end' }}
        >
          <Autocomplete
            sx={{ minWidth: 260, flex: 1 }}
            options={(allCars ?? []).slice().sort((a, b) => a.CarNumber - b.CarNumber)}
            getOptionLabel={carLabel}
            filterOptions={carFilter}
            isOptionEqualToValue={(a, b) => a.CarNumber === b.CarNumber}
            value={selectedCar}
            inputValue={carInput}
            onChange={(_e, v) => setSelectedCar(v)}
            onInputChange={(_e, v) => setCarInput(v)}
            autoHighlight
            openOnFocus
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            renderInput={(params) => (
              <TextField {...params} label="Car" size="small" placeholder="Type car # or institution" />
            )}
          />

          <Autocomplete
            sx={{ minWidth: 260, flex: 1 }}
            options={EVENT_TYPES}
            groupBy={(o) => o.group}
            getOptionLabel={(o) => o.label}
            filterOptions={eventFilter}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={eventType}
            inputValue={eventInput}
            onChange={(_e, v) => {
              setEventType(v);
              setValues({});
              setOverModes({});
            }}
            onInputChange={(_e, v) => setEventInput(v)}
            autoHighlight
            openOnFocus
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            renderInput={(params) => (
              <TextField {...params} label="Event Type" size="small" placeholder="Type to filter" />
            )}
          />

          {fields.map((f) => {
            const overMode = !!overModes[f.key];
            const showToggle = f.overAllowanceSec != null;
            const label = showToggle && overMode && f.overLabel ? f.overLabel : f.label;
            const helper = showToggle && overMode && f.overHelper ? f.overHelper : f.helper;
            return (
              <Box key={f.key} sx={{ minWidth: 180, display: 'flex', flexDirection: 'column' }}>
                <TextField
                  label={label}
                  size="small"
                  type={f.type}
                  value={values[f.key] ?? ''}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, [f.key]: e.target.value }))
                  }
                  helperText={helper}
                  required={f.required}
                />
                {showToggle && (
                  <FormControlLabel
                    sx={{ mt: 0.25, ml: 0.25 }}
                    control={
                      <Switch
                        size="small"
                        checked={overMode}
                        onChange={(_e, checked) => {
                          setOverModes((prev) => ({ ...prev, [f.key]: checked }));
                          // Clear the field so the operator doesn't
                          // accidentally submit a full-time value as an
                          // overage (or vice versa) after flipping the
                          // switch.
                          setValues((prev) => ({ ...prev, [f.key]: '' }));
                        }}
                      />
                    }
                    label={
                      <Typography variant="caption" color="text.secondary">
                        Enter time over allowance
                      </Typography>
                    }
                  />
                )}
              </Box>
            );
          })}

          <Box>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit}
              sx={{ minWidth: 120 }}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </Button>
          </Box>
        </Stack>

        {status && (
          <Alert severity={status.kind} sx={{ mt: 2 }} onClose={() => setStatus(null)}>
            {status.msg}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
